<script setup lang="ts">
import type { GrimoireItem } from '~/types/interfaces'

/**
 * 魔導書カード（GrimoireSlot）を並べるグリッド。
 *
 * このコンポーネントの責務は **選択/open の調停だけ**（1 枚だけ開く・再クリックで
 * 閉じる・items 入替で選択を掃除する）。`GrimoireSlot` のアクションボタンは
 * `open` prop で gate されるが、その `:open` 配線を**このコンテナが所有**するため、
 * 利用側は `:open` を渡す必要がない（＝配線もれで無反応になる #708 の再発を構造的に防ぐ）。
 *
 * アクション（read/play/program/…）が実際に何をするかは持たず、id を付けて上位へ
 * 中継するだけ（挙動は文脈で変わるのでページ／composable の責務）。
 * 詳細は doc/domain/cradle.md「カード操作のマトリックス」参照。
 */
const props = defineProps<{
  items: GrimoireItem[]
  /** クリスタル化ボタンを出す type の集合（未指定なら出さない）。 */
  crystallizeTypes?: string[]
}>()

const emit = defineEmits([
  // 選択/選択解除。open 状態は本コンポーネントが所有し、ページは detail/play 等の
  // ために「いま選ばれている item」を追跡する用途で受け取る（id | null）。
  'select',
  // カードの主アクション（挙動は上位が決める。ここは id を付けて中継するだけ）。
  'program',
  'read',
  'play',
  'detail',
  'crystallize',
  'rename',
  'delete',
])

// 「1 枚だけ開く」調停は本コンテナが所有する。再クリックでトグル（閉じる）。
const selectedId = ref<string | null>(null)

const onClickCard = (id: string) => {
  selectedId.value = selectedId.value === id ? null : id
  emit('select', selectedId.value)
}

// items が入れ替わって選択中の item が消えたら選択解除する
// （例: GrimoireSelector のカテゴリ絞り込みで開いていたカードが消えるケース）。
watch(
  () => props.items,
  (items) => {
    if (selectedId.value && !items.some(i => i.id === selectedId.value)) {
      selectedId.value = null
      emit('select', null)
    }
  },
)

const getGrimoireColor = (item: GrimoireItem) => {
  switch (item.type) {
    case 'user':
      return 'red'
    case 'encodia':
      return 'green'
    case 'crystal':
      return 'blue'
    case 'snapshot':
      return 'blue'
    case 'privilege':
      return 'brown'
    case 'encodia_reference':
      return 'teal'
    case 'github':
      return 'black'
    default:
      return 'red'
  }
}
</script>

<template>
  <div class="grimoire-card-grid">
    <v-container
      class="px-0 pt-0"
      fluid
    >
      <transition-group
        name="grimoire"
        tag="div"
        class="grimoire-wrapper"
      >
        <div
          v-for="item in items"
          :key="item.id"
          class="grimoire-item-wrapper"
          :data-grimoire-type="item.type"
        >
          <CradleGrimoireSlot
            :scene-id="item.id"
            :name="item.name"
            :type="item.type"
            :readonly="item.readonly"
            :open="item.id === selectedId"
            :color="getGrimoireColor(item)"
            :show-crystallize="crystallizeTypes?.includes(item.type) ?? false"
            :show-menu="item.type === 'user'"
            @click="onClickCard(item.id)"
            @program="emit('program', item.id)"
            @read="emit('read', item.id)"
            @play="emit('play', item.id)"
            @detail="emit('detail', item.id)"
            @crystallize="emit('crystallize', item.id)"
            @rename="emit('rename', item.id)"
            @delete="emit('delete', item.id)"
          />
        </div>
      </transition-group>
    </v-container>
  </div>
</template>

<style scoped>
/* グリッド + スクロール。centering43 前提の calc(N * var(--u)) サイジング（dvw 禁止）。 */
.grimoire-card-grid {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: scroll;
  padding-top: max(4px, calc(0.5 * var(--u)));
  scrollbar-color: #B2EBF2C0 #ffffff00;
  scrollbar-width: thin;
}

.grimoire-wrapper {
  --grid-gap: max(6px, calc(0.75 * var(--u)));

  width: 100%;
  padding: 0 var(--grid-gap);

  display: flex !important;
  flex-wrap: wrap !important;
  gap: var(--grid-gap);
}

.grimoire-item-wrapper {
  width: calc((100% - 4 * var(--grid-gap)) / 5);
  position: relative;
}

.grimoire-enter-active,
.grimoire-leave-active {
  transition: all 0.25s ease;
}

.grimoire-enter-from,
.grimoire-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.grimoire-move {
  transition: transform 0.25s ease;
}
</style>
